import { enablePatches, type Patch } from 'immer'
import { objectSetValueWithNestedKeyArray, objectGetValueWithNestedKeyArray } from '@/features/shared/tools/object'
import { $estDirty, type DirtyState, type Dirty } from '@/features/application/dirty'
import { shallowRef } from 'vue'
import { mergeModelState, reshapeModelState } from '@/features/domaine/model-state-tools'
import type { ModelState } from '@/features/domaine/types/techniques'
import type { NestedKeyOf } from '@/features/shared/types'

enablePatches()

export type DirtyStateCallback<TState extends object> = (state: DirtyState & TState) => DirtyState

export function useDirtyState<TModel extends object>() {
  const state = shallowRef<Dirty<TModel>>({})

  function applyPatch(patchs: Patch[]): void {
    dirtifyStateWithPatchs(state.value, patchs)
    state.value = { ...state.value }
  }

  function pathDirtyWithCallback<TState extends object>(
    dirtyState: Dirty<TModel>,
    otherState: ModelState<TModel, TState>,
    callback: DirtyStateCallback<TState>,
  ) {
    state.value = dirtifyStateWithCallback(dirtyState, otherState, callback)
  }

  function patchDirtyWithKeys(keysPath: NestedKeyOf<TModel>) {
    const newState = structuredClone(state.value)
    const keys = keysPath.split('.')
    while (keys.length > 0) {
      objectSetValueWithNestedKeyArray(newState, [...keys, '$estDirty'], true)
      keys.pop()
    }
    objectSetValueWithNestedKeyArray(newState, ['$estDirty'], true)
    state.value = newState
  }

  function $reset() {
    state.value = { $estDirty: false }
  }

  return { state, applyPatch, pathDirtyWithCallback, patchDirtyWithKeys, $reset }
}

/**
 * Exporter uniquement pour les test.
 * Ne pas utiliser directement
 */
export function dirtifyStateWithPatchs<TModel extends object>(state: Dirty<TModel>, patchs: Patch[]): Dirty<TModel> {
  for (const patch of patchs) {
    switch (patch.op) {
      case 'replace':
        traiteReplace(state, patch)
        break
      case 'remove':
        traiteRemove(state, patch)
        break
      case 'add':
        traiteAdd(state, patch)
        break
    }
  }

  return consolider$estDirty(state)
}

function dirtifyStateWithCallback<TModel extends object, TState extends object>(
  dirtyState: Dirty<TModel>,
  otherState: ModelState<TModel, TState>,
  callback: DirtyStateCallback<TState>,
): Dirty<TModel> {
  const newState = mergeModelState<TModel, DirtyState, ModelState<TModel, TState>>(dirtyState, otherState, {
    defaultA: {
      $estDirty: false,
    },
    compute: (state) => {
      if (state.$estDirty === true) return state
      const { $estDirty } = callback(state)
      return { ...state, $estDirty }
    },
    summarize: (sum, state) => {
      if (state.$estDirty !== true) return sum
      sum.$estDirty = true

      return sum
    },
  })

  return reshapeModelState<TModel, DirtyState>(newState, { $estDirty: false })
}

function createDirtyEmptyArray() {
  const newArray: any = []
  newArray.$estDirty = true
  return newArray
}

function consolider$estDirty<TModel extends object>(state: Dirty<TModel>): Dirty<TModel> {
  for (const key in state) {
    if (key.startsWith('$')) continue
    const value = (state as any)[key]
    const subState = consolider$estDirty(value) as DirtyState
    if (subState?.$estDirty === true) state.$estDirty = true
  }
  return state
}

function traiteReplace<TModel extends object>(state: Dirty<TModel>, patch: Patch): void {
  const arrayIndex = [...patch.path].pop()
  if (typeof arrayIndex === 'number') {
    // Descends (N -> N-1) l'élément d'un tableau du modèle
    const arrayPath = [...patch.path].slice(0, -1)
    const array = objectGetValueWithNestedKeyArray<Dirty<TModel>, Array<any>>(state, arrayPath)
    if (array === undefined || array === null) {
      // Le tableau n'existe pas encore dans le dirtyState
      // il n'y a pas d'élément à descendre
      objectSetValueWithNestedKeyArray(state, arrayPath, createDirtyEmptyArray())
    } else {
      // Descends de 1 place (N -> N-1) l'élément
      const element = array[arrayIndex + 1]
      array[arrayIndex] = element ?? null
        ; (array as DirtyState).$estDirty = true
    }
  } else if (typeof patch.value === 'object') {
    // Marque l'objet comme modifié
    state = objectSetValueWithNestedKeyArray(state, [...patch.path, $estDirty], true)
    // Applique le patch aux enfants
    for (const key in patch.value) {
      if (patch.value[key] === undefined || patch.value[key] === null) continue
      // Todo : A traiter en récursif ?
      objectSetValueWithNestedKeyArray(state, [...patch.path, key, $estDirty], true)
    }
  } else {
    objectSetValueWithNestedKeyArray(state, [...patch.path, $estDirty], true)
  }
}

function traiteAdd<TModel extends object>(state: Dirty<TModel>, patch: Patch): void {
  // Marque l'objet comme modifié
  if (typeof patch.value === 'object') {
    objectSetValueWithNestedKeyArray(state, [...patch.path, $estDirty], true)

    // Applique le patch aux enfants
    for (const key in patch.value) {
      if (patch.value[key] === undefined || patch.value[key] === null) continue
      objectSetValueWithNestedKeyArray(state, [...patch.path, key, $estDirty], true)
    }
  } else if (['number', 'boolean', 'string', 'undefined'].includes(typeof patch.value)) {
    const arrayIndex = [...patch.path].pop()
    if (typeof arrayIndex === 'number') {
      const arrayPath = [...patch.path].slice(0, -1)
      const array = objectGetValueWithNestedKeyArray<Dirty<TModel>, Array<any>>(state, arrayPath)
      if (array === undefined || array === null) {
        // Le tableau n'existe pas encore  dans le dirtyState
        objectSetValueWithNestedKeyArray(state, arrayPath, createDirtyEmptyArray())
      } else {
        // Flag le tableau $dirty
        ; (array as DirtyState).$estDirty = true
      }
    } else {
      objectSetValueWithNestedKeyArray(state, [...patch.path, $estDirty], true)
    }
  } else {
    // Probalement un tableau de primitives à implémenter plus tard
    console.warn('Dirtify : Add non traité', patch)
  }
}

function traiteRemove<TModel extends object>(state: Dirty<TModel>, patch: Patch): void {
  const arrayIndex = [...patch.path].pop()
  if (typeof arrayIndex === 'number') {
    // Obtient le tableau
    const arrayPath = [...patch.path].slice(0, -1)
    const array = objectGetValueWithNestedKeyArray<Dirty<TModel>, Array<any>>(state, arrayPath)
    if (array === undefined || array === null) {
      // Le tableau n'existe pas encore  dans le dirtyState
      // Rien a supprimer et juste le marquer $estDirty
      objectSetValueWithNestedKeyArray(state, arrayPath, createDirtyEmptyArray())
    } else {
      // Retire l'élément du tableau
      array.splice(arrayIndex, 1)
        ; (array as DirtyState).$estDirty = true
    }
  } else {
    console.warn('Dirtify : remove non traité', patch)
  }
}

